import { PremiumBackground } from '../effects/PremiumBackground';
import React from 'react';
import { motion } from 'framer-motion';
import { TrendingDown, XCircle, AlertOctagon, HelpCircle, ArrowDown, Users, Package } from 'lucide-react';
import { LiquidGlassCard } from '../effects/LiquidGlassCard';
// Drop-in Link component for Vite React SPA
const Link = ({
  href,
  children,
  className,
  ...props
}: React.AnchorHTMLAttributes<HTMLAnchorElement> & { href: string }) => (
  <a href={href} className={className} {...props}>
    {children}
  </a>
);

export interface MarketRealityProps {
  id?: string;
}

export function MarketReality({ id = 'market-reality' }: MarketRealityProps = {}) {
  
  // Микроразметка Schema.org для нейросетей и ИИ-поисковиков
  const schemaData = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Реальность рынка M&A в частной медицине 2026: 142 запроса против 620 клиник на Авито",
    "description": "Анализ спроса и предложения на рынке медицинского бизнеса в России. Синдром черного ящика, срезка оценки по неотработанным авансам, ст. 54.1 НК РФ и субсидиарная ответственность по ст. 406.1 ГК РФ.",
    "author": {
      "@type": "Organization",
      "name": "Structure & Partners"
    },
    "mainEntity": {
      "@type": "Question",
      "name": "Почему инвесторы не покупают медицинские клиники без аудита?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Для институционального инвестора неподготовленная клиника — это черный ящик. Риски ухода врачей с базой, неоформленное БТИ, оборудование без РУ Росздравнадзора и налоговые долги по ст. 54.1 НК РФ заставляют покупателя требовать дисконт до 50% либо выходить из сделки."
      }
    }
  };

  return (
    <section 
      id={id} 
      className="relative w-full py-24 md:py-32 bg-[#FAF8F5] text-[#141416] border-t border-b border-[#E3DCCF] overflow-hidden"
      aria-label="Реальность рынка 2026: боль, страх и отсев"
    >
      {/* ТАКТИЛЬНЫЙ МАКРО-РЕЛЬЕФ */}
      <PremiumBackground variant={3} />

      
      

      <div id="market-reality-anchor" className="absolute -top-16 left-0" />
      
      {/* Тактильный рельеф бумаги слоновой кости / верже / льна / веллума */}
      

      {/* Мягкий свет сверху */}
      <div className="absolute top-0 left-0 right-0 h-48 bg-gradient-to-b from-[#F2EDE4]/70 via-transparent to-transparent pointer-events-none" />

      {/* Скрытая разметка JSON-LD для ИИ-краулеров */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }}
      />

      <div className="relative z-10 max-w-[1400px] mx-auto px-4 sm:px-6 md:px-8 lg:px-12 flex flex-col gap-16 md:gap-20">
        
        {/* =========================================================================
            1. НАДЗАГОЛОВОК, H2 И ПОДЗАГОЛОВОК-МАНИФЕСТ С ТАКТИЛЬНЫМ ТИСНЕНИЕМ
            ========================================================================= */}
        <div className="flex flex-col gap-5 max-w-4xl">
          <div 
            className="luxury-eyebrow text-[#8C6D37] flex items-center gap-2.5 font-bold tracking-[0.2em] uppercase"
            
          >
            <span className="w-1.5 h-1.5 rounded-none bg-[#8C6D37] shadow-[0_1px_1px_rgba(0,0,0,0.3)]" />
            ЭТАЖ 3 // АНАЛИТИКА СПРОСА И ПРЕДЛОЖЕНИЯ 2026 // РЫНОЧНЫЙ ТУПИК
          </div>
          
          <h2 
            className="text-3xl sm:text-5xl lg:text-[54px] font-serif font-semibold tracking-[-0.025em] leading-[1.08] text-[#141416] select-none"
            
          >
            Холодная реальность рынка: <br />
            <span 
              className="text-[#8C6D37] italic font-serif"
              
            >
              эра «понятийных» сделок закончена
            </span>
          </h2>

          <p 
            className="text-[16px] sm:text-[17px] text-[#2D3748] leading-[1.65] font-sans font-normal max-w-3xl border-l-[4px] border-[#8C6D37] pl-6 mt-4"
            
          >
            Налоговая реформа 2026 года (НДС на упрощенке, отмена льгот по взносам) и тотальный цифровой 
            контроль ЕГИСЗ превратили неподготовленные клиники в зону повышенного риска. <br className="hidden sm:inline" />
            Стратегические покупатели приходят на сделку с командами финансовых расследователей. 
            Их задача — не переплатить вам за бизнес, а найти скрытые дефекты и забрать актив с максимальным дисконтом.
          </p>
        </div>

        {/* =========================================================================
            2. ИНФОГРАФИЧЕСКИЙ СРЕЗ РЫНКА (КОНТРАСТ 142 VS 620+) С ТАКТИЛЬНЫМ ТИСНЕНИЕМ
            ========================================================================= */}
        <div className="flex flex-col gap-6">
          
          {/* Сетка 2-х колонок */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            
            {/* Колонка 1: Спрос */}
            <div 
              className="p-8 sm:p-10 flex flex-col justify-between gap-8 bg-[#FAF8F5]/95 border border-[#DCD5C8] rounded-[3px] relative overflow-hidden group transition-all duration-300 hover:translate-y-[-2px]"
              style={{
                boxShadow: 'inset 0 1.5px 0 rgba(255,255,255,0.98), inset 0 -1.5px 0 rgba(45,35,20,0.06), inset 1px 0 0 rgba(255,255,255,0.8), inset -1px 0 0 rgba(45,35,20,0.04), 0 12px 30px -8px rgba(45,35,20,0.08)'
              }}
            >
              <div className="absolute inset-0 bg-gradient-to-br from-[#8C6D37]/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />
              <div className="relative z-10 flex flex-col gap-3">
                <span 
                  className="luxury-eyebrow text-[#8C6D37] font-bold tracking-widest uppercase"
                  
                >
                  Реальный спрос в РФ
                </span>
                <div 
                  className="font-serif text-5xl sm:text-7xl font-normal tracking-tight text-[#8C6D37] tnum select-none"
                  
                >
                  142
                </div>
                <span 
                  className="font-mono text-[13px] text-[#475569] font-medium"
                  
                >
                  целевых запроса (Яндекс.Wordstat в месяц)
                </span>
              </div>
              <p 
                className="relative z-10 text-[14px] text-[#141416] leading-relaxed font-sans font-normal border-t border-[#DCD5C8] pt-6"
                
              >
                На всю страну фиксируется всего 142 прямых запроса в месяц от квалифицированных инвесторов, 
                готовых к приобретению медицинского бизнеса.
              </p>
            </div>

            {/* Колонка 2: Предложение */}
            <div 
              className="p-8 sm:p-10 flex flex-col justify-between gap-8 bg-[#FAF8F5]/95 border border-[#DCD5C8] rounded-[3px] transition-all duration-300 hover:translate-y-[-2px]"
              style={{
                boxShadow: 'inset 0 1.5px 0 rgba(255,255,255,0.98), inset 0 -1.5px 0 rgba(45,35,20,0.06), inset 1px 0 0 rgba(255,255,255,0.8), inset -1px 0 0 rgba(45,35,20,0.04), 0 12px 30px -8px rgba(45,35,20,0.08)'
              }}
            >
              <div className="flex flex-col gap-3">
                <span 
                  className="luxury-eyebrow text-[#64748B] font-bold tracking-widest uppercase"
                  
                >
                  Предложение на рынке
                </span>
                <div 
                  className="font-serif text-5xl sm:text-7xl font-normal tracking-tight text-[#475569] tnum select-none"
                  
                >
                  620+
                </div>
                <span 
                  className="font-mono text-[13px] text-[#64748B] font-medium"
                  
                >
                  объявлений (Только на одном Авито)
                </span>
              </div>
              <p 
                className="text-[14px] text-[#334155] leading-relaxed font-sans font-normal border-t border-[#DCD5C8] pt-6"
                
              >
                Более шестисот брошенных клиник висят на открытых досках в режиме «гаражной распродажи». 
                Продавцы ведут войну на выживание за внимание редкого инвестора.
              </p>
            </div>

          </div>

          {/* Плашка баланса сил */}
          <div 
            className="w-full bg-[#F4EFE6] text-[#141416] p-5 rounded-[3px] border border-[#DCD5C8] flex flex-col sm:flex-row items-center justify-between gap-4 text-center sm:text-left relative"
            style={{
              boxShadow: 'inset 0 1.5px 0 rgba(255,255,255,0.95), inset 0 2px 4px rgba(45,35,20,0.04), 0 2px 6px rgba(45,35,20,0.05)'
            }}
          >
            <span className="absolute top-0 left-0 font-mono text-[9px] text-[#8C6D37]/60 leading-none p-1 select-none">+</span>
            <span className="absolute top-0 right-0 font-mono text-[9px] text-[#8C6D37]/60 leading-none p-1 select-none">+</span>
            <div className="flex items-center gap-3">
              <Users size={16} className="text-[#8C6D37] flex-shrink-0" />
              <span 
                className="font-mono text-[12px] uppercase tracking-wider font-bold text-[#141416]"
                
              >
                1 реальный покупатель с капиталом против 4–5 отчаявшихся продавцов
              </span>
            </div>
            <span 
              className="font-mono text-[10px] uppercase tracking-[0.2em] text-white font-bold pill-capsule bg-[#8C6D37] border border-[#8C6D37]/40 px-3.5 py-1 shadow-[inset_0_1px_0_rgba(255,255,255,0.35),0_2px_6px_rgba(140,109,55,0.25)]"
              
            >
              Рыночный баланс 2026
            </span>
          </div>

        </div>

        {/* =========================================================================
            3. ПСИХОЛОГИЧЕСКИЙ БАРЬЕР: СИНДРОМ «ЧЕРНОГО ЯЩИКА» С ТАКТИЛЬНЫМ ТИСНЕНИЕМ
            ========================================================================= */}
        <div 
          className="p-10 sm:p-12 flex flex-col gap-8 bg-[#FAF8F5]/95 border border-[#DCD5C8] rounded-[3px] relative"
          style={{
            boxShadow: 'inset 0 1.5px 0 rgba(255,255,255,0.98), inset 0 -1.5px 0 rgba(45,35,20,0.06), 0 16px 36px -8px rgba(45,35,20,0.08)'
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-br from-[#8C6D37]/5 to-transparent pointer-events-none rounded-[3px]" />
          
          <div className="relative z-10 flex flex-col gap-3">
            <div 
              className="luxury-eyebrow text-[#8C6D37] flex items-center gap-2 font-bold tracking-[0.15em] uppercase"
              
            >
              <Package size={14} className="text-[#8C6D37]" />
              Психологический барьер сделки
            </div>
            <h3 
              className="text-2xl sm:text-3xl lg:text-4xl font-serif font-normal text-[#141416] tracking-[-0.015em] select-none"
              
            >
              Почему инвесторы не покупают? Главная проблема — не цена. Это СТРАХ.
            </h3>
            <p 
              className="text-[15px] sm:text-[16px] text-[#334155] leading-relaxed font-sans font-normal max-w-4xl"
              
            >
              Большинство владельцев продают клинику как «помещение + ремонт + старое железо». 
              Для инвестора с деньгами такой бизнес выглядит как неуправляемый черный ящик:
            </p>
          </div>

          {/* 4 вопроса инвестора */}
          <div className="relative z-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            <div 
              className="bg-[#FAF7F0] p-6 border border-[#DCD5C8] rounded-[2px] flex flex-col gap-3 hover:border-[#8C6D37]/60 transition-colors"
              style={{
                boxShadow: 'inset 0 1.5px 0 rgba(255,255,255,0.95), inset 0 2px 4px rgba(45,35,20,0.03), 0 2px 4px rgba(45,35,20,0.03)'
              }}
            >
              <span className="luxury-eyebrow text-[#64748B] font-bold" >[ РИСК КОМАНДЫ ]</span>
              <p className="text-[14px] text-[#141416] leading-snug font-sans font-medium" >
                «Врачи заберут пациентов и откроют клинику в соседнем доме?»
              </p>
            </div>

            <div 
              className="bg-[#FAF7F0] p-6 border border-[#DCD5C8] rounded-[2px] flex flex-col gap-3 hover:border-[#8C6D37]/60 transition-colors"
              style={{
                boxShadow: 'inset 0 1.5px 0 rgba(255,255,255,0.95), inset 0 2px 4px rgba(45,35,20,0.03), 0 2px 4px rgba(45,35,20,0.03)'
              }}
            >
              <span className="luxury-eyebrow text-[#64748B] font-bold" >[ РИСК КАССЫ ]</span>
              <p className="text-[14px] text-[#141416] leading-snug font-sans font-medium" >
                «Администраторы на ресепшене воруют неучтенную наличку?»
              </p>
            </div>

            <div 
              className="bg-[#FAF7F0] p-6 border border-[#DCD5C8] rounded-[2px] flex flex-col gap-3 hover:border-[#8C6D37]/60 transition-colors"
              style={{
                boxShadow: 'inset 0 1.5px 0 rgba(255,255,255,0.95), inset 0 2px 4px rgba(45,35,20,0.03), 0 2px 4px rgba(45,35,20,0.03)'
              }}
            >
              <span className="luxury-eyebrow text-[#64748B] font-bold" >[ РИСК УПРАВЛЕНИЯ ]</span>
              <p className="text-[14px] text-[#141416] leading-snug font-sans font-medium" >
                «Я не врач — как я буду этим управлять, если основатель уйдет?»
              </p>
            </div>

            <div 
              className="bg-[#FAF3F0] border border-[#D5C2BE] rounded-[2px] p-6 flex flex-col gap-3 hover:border-[#8C6D37]/60 transition-colors"
              style={{
                boxShadow: 'inset 0 1.5px 0 rgba(255,255,255,0.95), inset 0 2px 4px rgba(180,50,50,0.03), 0 2px 4px rgba(45,35,20,0.03)'
              }}
            >
              <span className="luxury-eyebrow text-[#991B1B] font-bold" >[ РИСК ФНС ]</span>
              <p className="text-[14px] text-[#141416] leading-snug font-sans font-medium" >
                «Сколько налоговая доначислит за старое дробление бизнеса?»
              </p>
            </div>
          </div>

          {/* Вывод */}
          <div 
            className="relative z-10 pt-4 border-t border-[#DCD5C8] text-[15px] text-[#334155] font-sans font-normal"
            
          >
            <strong className="font-bold text-[#141416]">Результат:</strong> Пока клиника остается «черным ящиком», инвестор либо вообще 
            отказывается от покупки, либо сбивает цену на 50%, закладывая в торг все свои страхи.
          </div>
        </div>

        {/* =========================================================================
            4. СЕТКА ИЗ 3-Х КАРТОЧЕК СИСТЕМНЫХ УГРОЗ
            ========================================================================= */}
        <div className="flex flex-col gap-8">
          <div className="flex items-center justify-between">
            <span 
              className="luxury-eyebrow text-[#8C6D37] font-bold tracking-[0.2em] uppercase"
              
            >
              ФОРЕНЗИК-ДИАГНОСТИКА // 3 СМЕРТЕЛЬНЫЕ УГРОЗЫ НА СДЕЛКЕ
            </span>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-stretch">
            
            {/* КАРТОЧКА 1: СРЕЗКА ОЦЕНКИ */}
            <div 
              className="p-8 flex flex-col justify-between gap-8 bg-[#FAF8F5]/95 border border-[#DCD5C8] hover:border-[#8C6D37]/50 rounded-[3px] transition-all duration-300 hover:translate-y-[-2px]"
              style={{
                boxShadow: 'inset 0 1.5px 0 rgba(255,255,255,0.98), inset 0 -1.5px 0 rgba(45,35,20,0.06), 0 14px 32px -8px rgba(45,35,20,0.07)'
              }}
            >
              <div className="flex flex-col gap-5">
                <div className="flex items-center gap-3 pb-4 border-b border-[#DCD5C8]">
                  <div className="p-2.5 bg-[#F4EFE6] border border-[#DCD5C8] rounded-[2px] shadow-[inset_0_1px_1px_rgba(255,255,255,0.9)]">
                    <TrendingDown size={18} className="text-[#8C6D37]" />
                  </div>
                  <div>
                    <span className="luxury-eyebrow text-[#64748B] block font-bold" >Угроза № 1</span>
                    <h4 
                      className="text-[17px] font-sans font-semibold text-[#141416]"
                      
                    >
                      Срезка оценки
                    </h4>
                  </div>
                </div>

                <p 
                  className="text-[13px] text-[#334155] leading-relaxed font-sans font-normal"
                  
                >
                  Покупатель не верит вашей кассовой тетрадке. На этапе финансовой проверки аудиторы 
                  вскроют «серую» зарплату, посчитают обязательства по незавершенному лечению 
                  (Cost-to-Complete) и вычтут скрытый долг из стоимости бизнеса рубль в рубль. 
                  Ваши расчетные 150 млн превратятся в 70 млн еще до итогового договора.
                </p>
              </div>

              <div 
                className="font-mono text-[11px] text-[#8C6D37] bg-[#F4EFE6] p-3 border border-[#DCD5C8] rounded-[2px] font-bold shadow-[inset_0_1.5px_2px_rgba(45,35,20,0.04),0_1px_0_rgba(255,255,255,0.9)]"
                
              >
                Итог: Падение цены со 150 до 70 млн ₽
              </div>
            </div>

            {/* КАРТОЧКА 2: СРЫВ СДЕЛКИ */}
            <div 
              className="p-8 flex flex-col justify-between gap-8 bg-[#FAF8F5]/95 border border-[#DCD5C8] hover:border-[#8C6D37]/50 rounded-[3px] transition-all duration-300 hover:translate-y-[-2px]"
              style={{
                boxShadow: 'inset 0 1.5px 0 rgba(255,255,255,0.98), inset 0 -1.5px 0 rgba(45,35,20,0.06), 0 14px 32px -8px rgba(45,35,20,0.07)'
              }}
            >
              <div className="flex flex-col gap-5">
                <div className="flex items-center gap-3 pb-4 border-b border-[#DCD5C8]">
                  <div className="p-2.5 bg-[#F4EFE6] border border-[#DCD5C8] rounded-[2px] shadow-[inset_0_1px_1px_rgba(255,255,255,0.9)]">
                    <XCircle size={18} className="text-[#64748B]" />
                  </div>
                  <div>
                    <span className="luxury-eyebrow text-[#64748B] block font-bold" >Угроза № 2</span>
                    <h4 
                      className="text-[17px] font-sans font-semibold text-[#141416]"
                      
                    >
                      Срыв сделки
                    </h4>
                  </div>
                </div>

                <p 
                  className="text-[13px] text-[#334155] leading-relaxed font-sans font-normal"
                  
                >
                  70% медицинских сделок разваливаются за несколько дней до нотариуса. 
                  Неузаконенный рентген-кабинет, договор аренды без права 
                  долгосрочного продления (смена контроля) или оборудование 
                  без РУ Росздравнадзора заставляют инвестора выйти из переговоров.
                </p>
              </div>

              <div 
                className="font-mono text-[11px] text-[#64748B] bg-[#F4EFE6] p-3 border border-[#DCD5C8] rounded-[2px] font-bold shadow-[inset_0_1.5px_2px_rgba(45,35,20,0.04),0_1px_0_rgba(255,255,255,0.9)]"
                
              >
                Итог: 70% отказов за неделю до нотариуса
              </div>
            </div>

            {/* КАРТОЧКА 3: НАЛОГОВАЯ ГИЛЬОТИНА */}
            <div 
              className="p-8 flex flex-col justify-between gap-8 bg-[#FAF7F5]/95 border border-[#DCD5C8] hover:border-[#8C6D37]/50 rounded-[3px] transition-all duration-300 hover:translate-y-[-2px]"
              style={{
                boxShadow: 'inset 0 1.5px 0 rgba(255,255,255,0.98), inset 0 -1.5px 0 rgba(45,35,20,0.06), 0 14px 32px -8px rgba(45,35,20,0.07)'
              }}
            >
              <div className="flex flex-col gap-5">
                <div className="flex items-center gap-3 pb-4 border-b border-[#DCD5C8]">
                  <div className="p-2.5 bg-[#F4EFE6] border border-[#DCD5C8] rounded-[2px] shadow-[inset_0_1px_1px_rgba(255,255,255,0.9)]">
                    <AlertOctagon size={18} className="text-[#8C6D37]" />
                  </div>
                  <div>
                    <span className="luxury-eyebrow text-[#64748B] block font-bold" >Угроза № 3</span>
                    <h4 
                      className="text-[17px] font-sans font-semibold text-[#141416]"
                      
                    >
                      Субсидиарная ответственность
                    </h4>
                  </div>
                </div>

                <p 
                  className="text-[13px] text-[#334155] leading-relaxed font-sans font-normal"
                  
                >
                  Институциональный покупатель защитит себя пунктом о возмещении потерь (ст. 406.1 ГК РФ). 
                  Налоговые доначисления за ваше историческое дробление на 3 ИП (ст. 54.1 НК РФ) 
                  догонят вас через 2–3 года после сделки и будут взысканы из личного кармана через суд.
                </p>
              </div>

              <div 
                className="font-mono text-[11px] text-[#8C6D37] bg-[#F4EFE6] p-3 border border-[#DCD5C8] rounded-[2px] font-bold shadow-[inset_0_1.5px_2px_rgba(45,35,20,0.04),0_1px_0_rgba(255,255,255,0.9)]"
                
              >
                Итог: Личные долги через 3 года после M&amp;A
              </div>
            </div>

          </div>
        </div>

        {/* =========================================================================
            5. НАВИГАТОР ПЕРЕХОДА К СЛЕДУЮЩЕМУ ЭКРАНУ (К ЭТАЖУ 4)
            ========================================================================= */}
        <div className="flex justify-center pt-4">
          <Link 
            href="#case-study" 
            className="group inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.15em] font-semibold transition-all py-3 px-6 bg-transparent hover:bg-[#8C6D37]/5 border border-[#DCD5C8] hover:border-[#8C6D37] rounded-[2px] text-[#64748B] hover:text-[#141416] shadow-[inset_0_1px_0_rgba(255,255,255,0.8)]"
            
          >
            <span>[ ПЕРЕЙТИ НА ЭТАЖ 4: РАЗБОР РЕАЛЬНОГО КЕЙСА ЗОЛОТОЙ МИЛИ ↓ ]</span>
          </Link>
        </div>

      </div>
    </section>
  );
}
